import React, { useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { IconEye, IconEyeOff } from "@tabler/icons-react";

// UI copmonents
import {
  ActionIcon,
  PasswordInput as PasswordInputInitial,
  Flex,
  Input,
} from "@mantine/core";
import { PasswordRequirements } from "@pages/start/components/AccountActivateForm/formComponents/SetPassword/password-requirements/password-requirements";
// css
import { useColors } from "@utils/useColors";
import scopedClasses from "./set_password.module.css";
// types
import type { AccountActivateForm } from "src/services/accountActivation/accountActivationTypes";

const PasswordVisibilityToggle = React.memo<{
  isVisibleCurrently: boolean;
  handleClick: () => void;
  "data-testid": string;
}>(function PasswordVisibilityToggle(props) {
  const { gray50 } = useColors();

  const Component = useMemo(() => {
    return props.isVisibleCurrently ? IconEyeOff : IconEye;
  }, [props.isVisibleCurrently]);

  return (
    <ActionIcon
      variant="transparent"
      aria-label="toggle-password-visibility"
      onClick={props.handleClick}
      data-testid={props["data-testid"]}
    >
      <Component color={gray50} />
    </ActionIcon>
  );
});

const PasswordInput = PasswordInputInitial.withProps({
  classNames: {
    root: scopedClasses["input-root"],
    wrapper: scopedClasses["input-wrapper"],
    input: scopedClasses.input,
  },
  inputWrapperOrder: ["label", "input", "description", "error"],
  rightSection: "",
});

// Extract the props type of PasswordInput
type PasswordInputProps = React.ComponentProps<typeof PasswordInput> & {
  showErrorMessage?: boolean;
  "data-testid": string;
};

export const PasswordField: React.FC<PasswordInputProps> = (props) => {
  const { showErrorMessage = true, ...restOfProps } = props;
  const [passwordVisible, _setIsVisible] = useState(false);

  const togglePasswordVisibility = useCallback(() => {
    _setIsVisible(!passwordVisible);
  }, [passwordVisible]);

  const { t } = useTranslation();

  return (
    <>
      <PasswordInput
        {...restOfProps}
        visible={passwordVisible}
        error={Boolean(props.error)}
        placeholder={t("startPage.inputs.password")}
        rightSection={
          <PasswordVisibilityToggle
            handleClick={togglePasswordVisibility}
            isVisibleCurrently={passwordVisible}
            data-testid={`${props["data-testid"]}-toggle-visible`}
          />
        }
      />
      <Flex justify="space-between" gap="xs" w="100%">
        <Input.Error className={scopedClasses["input-error"]}>
          {showErrorMessage ? props.error : null}
        </Input.Error>
      </Flex>
    </>
  );
};

export const SetPassword: React.FC<{ form: AccountActivateForm }> = ({
  form,
}) => {
  return (
    <>
      {/* PASSWORD1 */}
      <PasswordField
        {...form.getInputProps("password")}
        data-testid="password-input"
      />
      {/* PASSWORD2 */}
      <PasswordField
        {...form.getInputProps("confirmPassword")}
        data-testid="password-confirm"
      />

      <PasswordRequirements
        passwordValue={form.values.password}
        shouldDisplayColors={form.isDirty("password")}
        containerClassName={scopedClasses.requirements}
      />
    </>
  );
};
